import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller, type Control } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getThread, inviteToThread, type InviteToThreadRequest, type Thread } from '../../../services/MessageService';
import { useNotification } from '../../../components/Notifications';
import { getUsers, newUser, type UserDTO } from '../../../services/AuthService';

export function Invite() {
  const { register, handleSubmit, formState: { errors }, control, reset, setValue } = useForm({
    defaultValues: {
      selectedUsers: [],
      emails: '',
      role: 'User'
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const dropdownRef = useRef(null);
  const emailInputRef = useRef(null);
  const { id } = useParams();
  const { addNotification } = useNotification();
  const [thread, setThread] = useState<Thread>({
    id: 0,
    createdId: 0,
    createdTs: null,
    recipients: [],
    subject: '',
    ts: null,
    messages: [],
    messagesCount: 0
  });
  const [users, setUsers] = useState<UserDTO[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadThread();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [thread]);

  const loadThread = () => {
    getThread(Number(id))
      .then(data => {
        console.log('Thread data:', data);
        setThread(data);
      })
      .catch(err => {
        addNotification(`Error loading message ${id} ${err.message}`, 'error');
      });
  };

  const loadUsers = () => {
    getUsers()
      .then(data => {
        console.log(`Loaded ${data.length} users`);
        const subscribed = new Map(thread.recipients.map((r) => [r.id, r]));
        setUsers(data.filter(u => !subscribed.has(u.id)));
      })
      .catch(err => {
        addNotification(`Error loading users`, 'error');
      });
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    `${user.firstName} ${user.lastName} ${user.email}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const req: InviteToThreadRequest = {
        threadId: thread.id,
        users: data.selectedUsers
      };
      const t = await inviteToThread(req)
      setLoading(false);
      reset();
      setShowEmailInput(false);
      setEmailInput('');
      addNotification('Users have been successfully invited', 'success')
      navigate(-1)

    } catch (err) {
      setLoading(false);
      setError(`Error sending invites: ${err.message}`);
    }
  };

  const roleOptions = ['Guest', 'User', 'Admin'];

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddEmail = async () => {
    if (emailInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      try {
        const user = await newUser({email:emailInput});

        setUsers([...users, user]);
        const selectedUsers = control._formValues.selectedUsers;
        setValue('selectedUsers', [...selectedUsers, user.id]);
        setEmailInput('');
        setShowEmailInput(false);
      } catch (err) {
        addNotification('Error adding new user ' + err.message, 'error');
      }
    } else {
      addNotification('Please enter a valid email address', 'error');
    }
  };

  const handleCancelEmail = () => {
    setEmailInput('');
    setShowEmailInput(false);
  };

  return (
    <div className="bg-gray-100 flex items-center justify-center">
      <div className="w-full space-y-6 bg-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">Invite Users</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowEmailInput(true)}
                className="flex text-indigo-500  h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
                aria-label="Add new user"
              >
                <i className="fas fa-plus"></i>
              </button>
              {showEmailInput && (
                <div className="flex items-center space-x-2">
                  <input
                    ref={emailInputRef}
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter email address"
                    className="pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400"
                    aria-label="Enter new email address"
                  />
                  <button
                    onClick={handleAddEmail}
                    className="text-white h-8 w-8 flex items-center justify-center rounded-full bg-indigo-500 hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
                    aria-label="Confirm email"
                  >
                    <i className="fas fa-check"></i>
                  </button>
                  <button
                    onClick={handleCancelEmail}
                    className="text-white h-8 w-8 flex items-center justify-center rounded-full bg-indigo-500 hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
                    aria-label="Cancel email input"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search emails..."
              className="pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400 w-full"
              aria-label="Search users by name or email"
            />
            <i
              className="fa-solid fa-magnifying-glass text-indigo-500 absolute right-3 top-1/2 transform -translate-y-1/2 text-lg"
            ></i>
          </div>
        </div>
        {error && (
          <p className="text-red-600 text-sm text-center">{error}</p>
        )}
        {loading && (
          <div className="text-center">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4 mx-auto"></div>
            <p className="text-gray-600">Sending invites...</p>
          </div>
        )}
        {!loading && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-1">
              <Controller
                name="selectedUsers"
                control={control}
                render={({ field }) => (
                  <div className="border border-gray-300 rounded-md bg-white max-h-60 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center px-3 py-2 hover:bg-indigo-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={user.id}
                            checked={field.value.includes(user.id)}
                            onChange={(e) => {
                              const newValue = e.target.checked
                                ? [...field.value, user.id]
                                : field.value.filter((id) => id !== user.id);
                              field.onChange(newValue);
                            }}
                            className="h-4 w-4 text-indigo-500 border-gray-300 rounded cursor-pointer focus:ring-indigo-600"
                            aria-label={`Select ${user.firstName} ${user.lastName}`}
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {user.firstName} {user.lastName} ({user.email})
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-500">No users found</p>
                    )}
                  </div>
                )}
              />
              {errors.selectedUsers && (
                <p className="text-red-600 text-sm">{errors.selectedUsers.message}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
              >
                Send Invites
              </button>
              <div
                onClick={() => navigate(-1)}
                className="cursor-pointer flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 text-center"
              >
                Cancel
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};